import i18n from 'i18n';
import { isNumber } from 'utils/common';
import { getRateByClass } from 'utils/fieldglass';
import * as Yup from 'yup';

import { Job } from '@libs/graphql-types';

import {
  isBayerJob as getIsBayerJob,
  isWtwJob as getIsWtwJob,
} from './security-id-utils';

// Helper function to create a dynamic schema for questions and qualifications
const createDynamicSchema = (
  items: Array<{ mandatory: boolean; recordId: string }>,
) => {
  const schema: Record<string, Yup.StringSchema> = {};

  items.forEach((item) => {
    if (item.mandatory) {
      schema[item.recordId] = Yup.string().required(
        i18n.t('applicationForm.validation.required', { ns: 'jobForm' }),
      );
    } else {
      schema[item.recordId] = Yup.string();
    }
  });

  return Yup.object().shape(schema);
};

export const createApplicationFormSchema = (job: Job) => {
  const questions = JSON.parse(job?.striive_job?.questions || '[]').filter(
    (i: any) => i.isCandidate,
  );
  const qualifications = JSON.parse(job?.striive_job?.qualifications || '[]');
  const isBayerJob = getIsBayerJob(job);
  const isWtwJob = getIsWtwJob(job);
  // Note: Duke Energy uses the same validation as default (requires ssnLast4Digits)
  // so isDukeEnergyJob check is not needed in the validation logic

  return Yup.object().shape({
    basicInfo: Yup.object().shape({
      firstName: Yup.string().required(
        i18n.t('applicationForm.validation.firstNameRequired', {
          ns: 'jobForm',
        }),
      ),
      lastName: Yup.string().required(
        i18n.t('applicationForm.validation.lastNameRequired', {
          ns: 'jobForm',
        }),
      ),
      // dateOfBirth: Yup.date()
      //   .typeError('Please enter a valid date in DD/MM/YYYY format')
      //   .required('Date of birth is required')
      //   .max(new Date(), 'Date of birth cannot be in the future')
      //   .min(new Date(1900, 0, 1), 'Date of birth cannot be before year 1900'),
      jobPostingId: Yup.string().required('Job ID is required'),
      visibleToAllBuyers: Yup.boolean(),
      visibleToSelectedBuyers: Yup.boolean(),
      availableToAllBuyers: Yup.boolean(),
      availableToSelectedBuyers: Yup.boolean(),
    }),
    resume: Yup.object()
      .shape({
        filename: Yup.string(),
        newCvUpload: Yup.mixed(),
      })
      .test(
        'resume-required',
        i18n.t('applicationForm.validation.resumeRequired', { ns: 'jobForm' }),
        function (value) {
          const { filename, newCvUpload } = value || {};
          return !!(filename || newCvUpload);
        },
      ),
    // .test('resume-required', 'Please upload a resume', function (value) {
    //   const { newCvUpload } = value || {};
    //   return !!newCvUpload?.hash;
    // }),
    details: Yup.object().shape({
      monthOfBirth: Yup.number()
        .required('Month of birth is required')
        .integer('Month must be a whole number')
        .min(1, 'Month must be between 1 and 12')
        .max(12, 'Month must be between 1 and 12'),
      dayOfBirth: Yup.number()
        .required('Day of birth is required')
        .integer('Day must be a whole number')
        .min(1, 'Day must be between 1 and 31')
        .max(31, 'Day must be between 1 and 31'),
      ...(isBayerJob || isWtwJob
        ? {}
        : {
            ssnLast4Digits: Yup.string()
              .required('Last 4 digits of SSN is required')
              .matches(/^\d{4}$/, 'Must be exactly 4 digits')
              .length(4, 'Must be exactly 4 digits'),
          }),
      ...(isWtwJob
        ? {
            ssnOrPassportLast3Digits: Yup.string()
              .required('Last 3 digits of SSN or Passport Number is required')
              .matches(/^\d{3}$/, 'Must be exactly 3 digits')
              .length(3, 'Must be exactly 3 digits'),
          }
        : {}),
      securityId: Yup.string(),
      confirmSecurityId: Yup.string(),
      availableDate: Yup.date().required(),
      submittedToOtherJobs: Yup.boolean(),
      workerPayType: Yup.string().required('Worker Pay Type is required'),
      displayWorkforceRecord: Yup.boolean(),
      education: Yup.string(),
      county: Yup.string(),
      previouslyWorked: Yup.string(),

      siteCode: Yup.object().nullable().required('Site Code is required'),

      // locationCode: Yup.array()
      //   .of(
      //     Yup.object().shape({
      //       value: Yup.string().required(),
      //       text: Yup.string().required(),
      //     }),
      //   )
      //   .nullable()
      //   .transform((value) => (value === null ? [] : value))
      //   .min(1, 'Location Code is required')
      //   .required('Location Code is required'),
      recruitingSource: Yup.string(),
      veteran: Yup.string(),
    }),
    additionalInfo: Yup.object().shape({
      languagesSpoken: Yup.string(),
      microsoftWordScore: Yup.number()
        .nullable()
        .min(0, 'Score must be between 0 and 100')
        .max(100, 'Score must be between 0 and 100'),
      microsoftExcelScore: Yup.number()
        .nullable()
        .min(0, 'Score must be between 0 and 100')
        .max(100, 'Score must be between 0 and 100'),
      typingScoreWPM: Yup.number()
        .nullable()
        .min(0, 'Score must be 0 or greater'),
      dataEntryAlpha: Yup.number()
        .nullable()
        .min(0, 'Score must be 0 or greater'),
      callCenterReferenceManualScore: Yup.number()
        .nullable()
        .min(0, 'Score must be between 0 and 100')
        .max(100, 'Score must be between 0 and 100'),
      callCenterDataEntryScore: Yup.number()
        .nullable()
        .min(0, 'Score must be 0 or greater'),
      callCenterListeningSkillsScore: Yup.number()
        .nullable()
        .min(0, 'Score must be between 0 and 100')
        .max(100, 'Score must be between 0 and 100'),
      customerServiceMindsetSurveyScore: Yup.number()
        .nullable()
        .min(0, 'Score must be between 0 and 100')
        .max(100, 'Score must be between 0 and 100'),
      callCenterSpanishEnglishBilingualScore: Yup.number()
        .nullable()
        .min(0, 'Score must be between 0 and 100')
        .max(100, 'Score must be between 0 and 100'),
      callCenterTelephoneEtiquetteScore: Yup.number()
        .nullable()
        .min(0, 'Score must be between 0 and 100')
        .max(100, 'Score must be between 0 and 100'),
      spanishEnglishBilingualScore: Yup.number()
        .nullable()
        .min(0, 'Score must be between 0 and 100')
        .max(100, 'Score must be between 0 and 100'),
      spanishCallCenterEtiquetteScore: Yup.number()
        .nullable()
        .min(0, 'Score must be between 0 and 100')
        .max(100, 'Score must be between 0 and 100'),
      analyticalSkillsScore: Yup.number()
        .nullable()
        .min(0, 'Score must be between 0 and 100')
        .max(100, 'Score must be between 0 and 100'),
      readingComprehension: Yup.string(),
      basicComputerLiteracy: Yup.string(),
      callCenterScenarios: Yup.string(),
      microsoftOffice: Yup.string(),
      msInternetExplorer: Yup.string(),
      businessEtiquette: Yup.string(),
      followingWritingInstructions: Yup.string(),
      hireSmartAssessmentSurvey: Yup.string(),
      additionalInformation: Yup.string(),
    }),
    rateInfo: Yup.object().when('details.workerPayType', ((
      [workerPayType]: [any],
      schema: any,
    ) => {
      const rate = getRateByClass(
        job,
        'ST',
        workerPayType ? { payType: workerPayType } : undefined,
      );
      const max = isNumber(rate?.billAmount)
        ? rate?.billAmount
        : Number.MAX_VALUE;

      return schema.shape({
        standardRate: Yup.number()
          .nullable()
          .min(0, 'Pay rate must be greater than 0')
          .max(
            max || Number.MAX_VALUE,
            max
              ? `Pay rate cannot exceed the requested max rate of ${rate.billAmountFormated}`
              : 'Invalid pay rate',
          ),
      });
    }) as any),
    questions: createDynamicSchema(questions),
    qualifications: createDynamicSchema(qualifications),
    additionalNotes: Yup.string(),
  });
};
